import { Stack, Tooltip, Typography } from '@mui/material';
import {
  type RestoreAccount,
  type SourceAccount,
  CloudAccountType,
  type Account,
} from '@repo/api-gw-sdk';

import {
  AccountNameOptionMenu,
  RenameAccount,
} from '@/components/accounts/AccountNameOptionMenu';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import {
  getAccountType,
  isRestoreAccount,
  isSourceAccount,
} from '@/utils/accounts';

export const HOST_PROJECT_ID_HELP_TEXT =
  'Required if your project uses a Shared VPC. This is the project that contains the network resources (VPC/subnets) used by the restore target.';

export const awsAccountDetails = (
  account: Account | SourceAccount | RestoreAccount,
  mutate: () => Promise<void>,
  editMode: boolean,
  setEditMode: (accountId: string | undefined) => void
) => {
  const accountType = getAccountType(account);
  const renameSupported =
    accountType === CloudAccountType.Source ||
    accountType === CloudAccountType.Restore;
  return {
    title: 'Account details',
    fields: [
      {
        title: 'Display name',
        id: 'display-name',
        value: renameSupported ? (
          <RenameAccount
            account={account}
            mutate={mutate}
            editMode={editMode}
            onClose={() => setEditMode(undefined)}
          />
        ) : (
          <CopyToClipboardWrapper textToCopy={account.name}>
            <Typography>{account.name}</Typography>
          </CopyToClipboardWrapper>
        ),
        actionsMenu: renameSupported ? (
          <AccountNameOptionMenu
            account={account}
            mutate={mutate}
            editMode={editMode}
            onEditNameOptionClick={() => setEditMode(account.id)}
          />
        ) : null,
      },
      {
        title: 'Account alias',
        id: 'account-alias',
        value: (
          <CopyToClipboardWrapper
            textToCopy={account.providerAccountName || ''}
          >
            <Typography>{account.providerAccountName || '--'}</Typography>
          </CopyToClipboardWrapper>
        ),
      },
      {
        title: 'Account ID',
        id: 'account-id',
        value: (
          <CopyToClipboardWrapper textToCopy={account.providerAccountId}>
            <Typography>{account.providerAccountId}</Typography>
          </CopyToClipboardWrapper>
        ),
      },
    ],
  };
};

export const azureAccountDetails = (
  subscription: Account | SourceAccount | RestoreAccount,
  mutate: () => Promise<void>,
  editMode: boolean,
  setEditMode: (accountId: string | undefined) => void
) => {
  const accountType = getAccountType(subscription);
  const renameSupported =
    accountType === CloudAccountType.Restore ||
    accountType === CloudAccountType.Source;
  const azureResourceGroupName = isRestoreAccount(subscription)
    ? subscription.restoreAccountAttributes.azure?.resourceGroupName
    : !isSourceAccount(subscription)
      ? subscription.azureResourceGroupName
      : undefined;
  return {
    title: 'Subscription details',
    fields: [
      {
        title: 'Display name',
        id: 'display-name',
        value: renameSupported ? (
          <RenameAccount
            account={subscription}
            mutate={mutate}
            editMode={editMode}
            onClose={() => setEditMode(undefined)}
          />
        ) : (
          <Typography>{subscription.name}</Typography>
        ),
        actionsMenu: renameSupported ? (
          <AccountNameOptionMenu
            account={subscription}
            mutate={mutate}
            editMode={editMode}
            onEditNameOptionClick={() => setEditMode(subscription.id)}
          />
        ) : null,
      },
      {
        title: 'Subscription name',
        id: 'subscription-name',
        value: (
          <CopyToClipboardWrapper
            textToCopy={subscription.providerAccountName || ''}
          >
            <Typography>{subscription.providerAccountName || '--'}</Typography>
          </CopyToClipboardWrapper>
        ),
      },
      {
        title: 'Subscription ID',
        id: 'subscription-id',
        value: (
          <CopyToClipboardWrapper textToCopy={subscription.providerAccountId}>
            <Typography>{subscription.providerAccountId}</Typography>
          </CopyToClipboardWrapper>
        ),
      },
      ...(accountType === CloudAccountType.Restore
        ? [
            {
              title: 'Allowed resource groups',
              id: 'allowed-resource-groups',
              value: azureResourceGroupName ? (
                <CopyToClipboardWrapper textToCopy={azureResourceGroupName}>
                  <Typography>{azureResourceGroupName}</Typography>
                </CopyToClipboardWrapper>
              ) : (
                'All'
              ),
            },
          ]
        : []),
    ],
  };
};

export const gcpAccountDetails = (
  project: Account | SourceAccount | RestoreAccount,
  mutate: () => Promise<void>,
  editMode: boolean,
  setEditMode: (accountId: string | undefined) => void
) => {
  const accountType = getAccountType(project);
  const renameSupported =
    accountType === CloudAccountType.Restore ||
    accountType === CloudAccountType.Source;
  return {
    title: 'Project details',
    fields: [
      {
        title: 'Display name',
        id: 'display-name',
        value: renameSupported ? (
          <RenameAccount
            account={project}
            mutate={mutate}
            editMode={editMode}
            onClose={() => setEditMode(undefined)}
          />
        ) : (
          <Typography>{project.name}</Typography>
        ),
        actionsMenu: renameSupported ? (
          <AccountNameOptionMenu
            account={project}
            mutate={mutate}
            editMode={editMode}
            onEditNameOptionClick={() => setEditMode(project.id)}
          />
        ) : null,
      },
      {
        title: 'Project name',
        id: 'project-name',
        value: (
          <CopyToClipboardWrapper
            textToCopy={project.providerAccountName || ''}
          >
            <Typography>{project.providerAccountName || '--'}</Typography>
          </CopyToClipboardWrapper>
        ),
      },
      {
        title: 'Project ID',
        id: 'project-id',
        value: (
          <CopyToClipboardWrapper textToCopy={project.providerAccountId}>
            <Typography>{project.providerAccountId}</Typography>
          </CopyToClipboardWrapper>
        ),
      },
      ...(isRestoreAccount(project)
        ? [
            {
              id: 'host-project',
              title: (
                <Stack direction='row' alignItems='center' gap='4px'>
                  <Typography component='span' className='truncate'>
                    Host project ID
                  </Typography>
                  <Tooltip title={HOST_PROJECT_ID_HELP_TEXT}>
                    <i className='material-symbols-help-outline-rounded h-[20px] w-[20px] p-[2px] text-(--mui-palette-text-secondary) opacity-0 group-hover/help:opacity-100 transition-opacity' />
                  </Tooltip>
                </Stack>
              ),
              value: (
                <Typography>
                  {project.restoreAccountAttributes.gcp?.hostProjectId || '--'}
                </Typography>
              ),
            },
          ]
        : []),
    ],
  };
};
